import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {BehaviorSubject, combineLatest, ReplaySubject, Subject, Subscription} from 'rxjs';
import {SelectFilterElement} from '../../../../../components/common/select-filter/select-filter.model';
import {ToastControl} from '../../../../../controls/core/toast/toast.control';
import {LocalityService} from '../../../../../services/locality/locality.service';
import {AddressModel} from '../../../entity/address.model';
import {Page} from '../../../../../shared/model/core/page.model';
import {LocalityModel} from '../../../localization/locality/api/entity/locality.model';
import {AuthService} from '../../../../auth/api/service/auth.service';
import {CountryFindAllService} from '../../../localization/country/api/service/countryFindAll.service';
import {CountryModel} from '../../../localization/country/api/entity/country.model';
import {AddressSavePayload} from '../../model/addressSave.payload';
import {AccountSummary} from '../../../../../shared/model/account/accountSummary.model';

@Component({
    selector: 'app-edit-address',
    templateUrl: './edit-address.component.html',
    styleUrls: ['./edit-address.component.scss'],
    providers: [
        CountryFindAllService
    ]
})
export class EditAddressComponent implements OnInit, OnDestroy, OnChanges {

    @Input() title= 'Adresse';
    @Input() cardTitle = 'Adresse';
    @Input() parentFormGroup: UntypedFormGroup;
    @Input() entity: AddressModel;
    @Input() initialPayload: AddressSavePayload;
    @Input() inCard = true;
    @Input() displayRecipientName = true;
    @Input() displayStreet = true;
    @Input() displayNumber = true;
    @Input() displayUrl = false;
    @Input() displayNavigationInstructions = false;
    @Input() account: AccountSummary;
    @Input() country: CountryModel;

    initialLoad = true;

    formAddressGroup: UntypedFormGroup;

    localitiesByZipCode: ReplaySubject<Map<string, any>> = new ReplaySubject<Map<string, any>>(1);
    localitiesByName: ReplaySubject<Map<string, any>> = new ReplaySubject<Map<string, any>>(1);

    countries: BehaviorSubject<SelectFilterElement<string, CountryModel>[]> =
        new BehaviorSubject<SelectFilterElement<string, CountryModel>[]>([]);
    currentCountry = undefined;
    addressSet = false;
    /** Subject that emits when the component has been destroyed. */
    private _onDestroy = new Subject<void>();

    subscriptions: Subscription[] = [];
    accountCountryCode: string;

  constructor(private formBuilder: UntypedFormBuilder,
              private localityService: LocalityService,
              private authService: AuthService,
              private countryFindAllService: CountryFindAllService,
  ) {
      this.accountCountryCode = this.authService.accountSnapshot?.summary?.country?.value;
      this.formAddressGroup = this.formBuilder.group({
          id: new UntypedFormControl(undefined),
          recipientName: new UntypedFormControl(undefined),
          street: new UntypedFormControl(undefined),
          number: new UntypedFormControl(undefined),
          zipCode: new UntypedFormControl(undefined),
          locality: new UntypedFormControl(undefined),
          url: new UntypedFormControl(undefined),
          navigationInstructions: new UntypedFormControl(undefined),
          country: new UntypedFormControl(this.accountCountryCode ? this.accountCountryCode : 'FR'),
      });
  }

    ngOnInit() {
    this.countryAddressControl.valueChanges
        .subscribe((value) => {
            // HACK: Currently this is triggered 40+ and generate 40+ http calls
            if (value !== this.currentCountry) {
                this.currentCountry = value;
                // console.logByType'country changed');
                if (this.autocompleteZipCodeLocalityOn()) {
                    this.searchLocalitiesByName(this.localityAddressControl.value);
                    this.searchLocalitiesByZipCode(this.zipCodeAddressControl.value);
                }
                this.localitiesByName.next(new Map<string, LocalityModel>());
                this.localitiesByZipCode.next(new Map<string, LocalityModel>());
            }

        });
    this.initialLoad = false;

    this.subscriptions.push(this.countryFindAllService.entities$.subscribe(value =>
        this.countries.next(value.map(c => new SelectFilterElement(c.value, c.value, c)))));
    this.countryFindAllService.findAll();

  }


    ngOnChanges(changes: SimpleChanges): void {
      const parentFormGroupChange = changes['parentFormGroup'];
      if ((parentFormGroupChange && parentFormGroupChange.currentValue)) {
          this.enrichFormGroup();
      }

      const entityChange = changes['entity'];
      if (entityChange && this.entity) {
          this.entityLoaded(this.entity);
      }

      if (changes['initialPayload'] && this.initialPayload) {
          this.formAddressGroup.patchValue(this.initialPayload, {onlySelf: true, emitEvent: false});
          this.addressSet = true;
      }

      if (changes['country']) {
          if (this.country && this.addressSet === false) {
              this.countryAddressControl.setValue(this.country.value);
          }
      }
    }



    ngOnDestroy(): void {
        this._onDestroy.next();
        this._onDestroy.complete();
        this.subscriptions.forEach(value => value.unsubscribe());
    }

    entityLoaded(address: AddressModel) {
      this.formAddressGroup.patchValue(address);
      this.addressSet = true;
    }


    private enrichFormGroup() {
        this.parentFormGroup.removeControl('address');
        this.parentFormGroup.addControl('address', this.formAddressGroup);

    }

    searchLocalitiesByZipCode(zipCode: string){
        if (zipCode) {
            this.localityService.findByZipCode(this.countryAddressControl.value, zipCode, 0, 25)
                .then((results: Page<LocalityModel>) => {
                    this.localitiesByZipCode.next(this.convertToMap(results));
                });
        } else {
            this.localitiesByZipCode.next(new Map<string, LocalityModel>());
        }
    }

    displayZipCode =  (zipCode: string) => {
        return zipCode;
    }

    valueZipCode = (value: LocalityModel) => {
        return value.zipCode;
    }

    displayZipCodeOption = (value: LocalityModel) => {
        return value.zipCode + ' - ' + value.name;
    }

    searchLocalitiesByName(name: string){
      if (name) {
          this.localityService.findByName(this.countryAddressControl.value, name, 0, 25)
              .then((results: Page<LocalityModel>) => {
                  this.localitiesByName.next(this.convertToMap(results));
              });
      } else {
          this.localitiesByName.next(new Map<string, LocalityModel>());
      }
    }

    displayLocality = (locality: string) => {
        return locality;
    }

    valueLocality = (value: LocalityModel) => {
        return value.name;
    }

    displayLocalityOption = (value: LocalityModel) => {
        return value.name + ' (code postal : ' + value.zipCode + ')';
    }

    selectLocality(locality: LocalityModel) {
        this.zipCodeAddressControl.patchValue(locality.zipCode);
    }

    selectZipCode(locality: LocalityModel) {
        this.localityAddressControl.patchValue(locality.name);
    }

    private convertToMap(results: Page<LocalityModel>) {
      const map = new Map<string, LocalityModel>();
      results.content.forEach(locality => {
          const id: string = locality.country.value + locality.zipCode + locality.name;
          map.set(id, locality);
      });
      return map;
    }

    autocompleteZipCodeLocalityOff() {
        return !this.autocompleteZipCodeLocalityOn();
    }

    autocompleteZipCodeLocalityOn() {
        return this.countryAddressControl && (this.countryAddressControl.value === 'BE' || this.countryAddressControl.value === 'FR');
    }

    get countryAddressControl() {
      return this.formAddressGroup.get('country');
    }

    get localityAddressControl() {
        return this.formAddressGroup.get('locality');
    }
    get zipCodeAddressControl() {
        return this.formAddressGroup.get('zipCode');
    }

    filterCountry = (criteria: string, country: CountryModel) => {
        return country.label.toUpperCase().startsWith(criteria.toUpperCase());
    }
}
